import { findColumn, locateBlockById } from "../find";
import { isGroup, isRow } from "../sections";
import type { DocumentSection, EmailDocument } from "../types";
import {
  blockMultiSelectionFromBlockId,
  emptyBlockMultiSelection,
  getPrimaryBlockId,
  type BlockMultiSelection,
} from "./block-multi-selection";

export type ColumnSiblingList = {
  rowId: string;
  columnId: string;
  blockIds: string[];
};

function orderIdsInSiblingList(
  blockIds: string[],
  siblingBlockIds: string[],
): string[] {
  const selected = new Set(blockIds);
  return siblingBlockIds.filter(id => selected.has(id));
}

function siblingListsEqual(
  left: ColumnSiblingList | null,
  right: ColumnSiblingList | null,
): boolean {
  if (left === null || right === null) {
    return false;
  }

  return left.rowId === right.rowId && left.columnId === right.columnId;
}

export function resolveColumnSiblingList(
  document: EmailDocument,
  blockId: string,
): ColumnSiblingList | null {
  const located = locateBlockById(document, blockId);
  if (!located) {
    return null;
  }

  const column = findColumn(document, located.rowId, located.columnId);
  if (!column) {
    return null;
  }

  return {
    rowId: located.rowId,
    columnId: located.columnId,
    blockIds: column.blocks.map(block => block.id),
  };
}

export function resolveColumnSiblingListForSelection(
  document: EmailDocument,
  selection: Pick<BlockMultiSelection, "selectedBlockIds" | "primaryBlockId">,
): ColumnSiblingList | null {
  const primary = getPrimaryBlockId(selection);
  if (primary !== null) {
    return resolveColumnSiblingList(document, primary);
  }

  const first = selection.selectedBlockIds[0];
  if (first === undefined) {
    return null;
  }

  return resolveColumnSiblingList(document, first);
}

export function applyBlockSelectionClick(args: {
  document: EmailDocument;
  current: BlockMultiSelection;
  blockId: string;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
}): BlockMultiSelection {
  const { document, current, blockId, metaKey, ctrlKey, shiftKey } = args;
  const targetList = resolveColumnSiblingList(document, blockId);
  if (targetList === null) {
    return current;
  }

  const currentList = resolveColumnSiblingListForSelection(document, current);
  const additive = metaKey || ctrlKey;

  if (shiftKey && !additive && currentList !== null && siblingListsEqual(currentList, targetList)) {
    const anchorId =
      current.anchorBlockId !== null && targetList.blockIds.includes(current.anchorBlockId)
        ? current.anchorBlockId
        : getPrimaryBlockId(current) ?? blockId;
    const anchorIndex = targetList.blockIds.indexOf(anchorId);
    const targetIndex = targetList.blockIds.indexOf(blockId);
    if (anchorIndex < 0 || targetIndex < 0) {
      return blockMultiSelectionFromBlockId(blockId);
    }

    const start = Math.min(anchorIndex, targetIndex);
    const end = Math.max(anchorIndex, targetIndex);
    return {
      selectedBlockIds: targetList.blockIds.slice(start, end + 1),
      primaryBlockId: blockId,
      anchorBlockId: anchorId,
    };
  }

  if (additive) {
    if (currentList === null || !siblingListsEqual(currentList, targetList)) {
      return blockMultiSelectionFromBlockId(blockId);
    }

    const alreadySelected = current.selectedBlockIds.includes(blockId);
    if (alreadySelected) {
      const nextIds = current.selectedBlockIds.filter(id => id !== blockId);
      if (nextIds.length === 0) {
        return emptyBlockMultiSelection();
      }

      const ordered = orderIdsInSiblingList(nextIds, targetList.blockIds);
      const primaryBlockId =
        current.primaryBlockId !== null && ordered.includes(current.primaryBlockId)
          ? current.primaryBlockId
          : ordered[ordered.length - 1] ?? null;

      return {
        selectedBlockIds: ordered,
        primaryBlockId,
        anchorBlockId: blockId,
      };
    }

    const ordered = orderIdsInSiblingList(
      [...current.selectedBlockIds, blockId],
      targetList.blockIds,
    );
    return {
      selectedBlockIds: ordered,
      primaryBlockId: blockId,
      anchorBlockId: blockId,
    };
  }

  return blockMultiSelectionFromBlockId(blockId);
}

function collectBlocksFromSections(
  sections: readonly DocumentSection[],
): string[] {
  const blockIds: string[] = [];

  for (const section of sections) {
    const rows = isGroup(section) ? section.rows : isRow(section) ? [section] : [];
    for (const row of rows) {
      for (const column of row.columns) {
        for (const block of column.blocks) {
          blockIds.push(block.id);
        }
      }
    }
  }

  return blockIds;
}

function sectionsForBodySelectAll(document: EmailDocument): DocumentSection[] {
  const { sections } = document;
  if (sections.length <= 1) {
    return [...sections];
  }
  if (sections.length === 2) {
    return [sections[1]];
  }
  return sections.slice(1, -1);
}

export function collectBodyBlockIds(document: EmailDocument): string[] {
  return collectBlocksFromSections(sectionsForBodySelectAll(document));
}

export function collectAllDocumentBlockIds(document: EmailDocument): string[] {
  return collectBlocksFromSections(document.sections);
}

export function selectAllBlocksInContext(
  document: EmailDocument,
  current: BlockMultiSelection,
): BlockMultiSelection {
  const currentList = resolveColumnSiblingListForSelection(document, current);
  const blockIds =
    currentList !== null && currentList.blockIds.length > 0
      ? currentList.blockIds
      : collectBodyBlockIds(document);

  if (blockIds.length === 0) {
    return emptyBlockMultiSelection();
  }

  return {
    selectedBlockIds: [...blockIds],
    primaryBlockId: blockIds[blockIds.length - 1] ?? null,
    anchorBlockId: blockIds[0] ?? null,
  };
}
